import { Call } from '@twilio/voice-sdk'
import { makeAutoObservable } from 'mobx'
import { nanoid } from 'nanoid'
import { type TwilioContact } from 'entities/Call/model/TwilioContact'
import { type TwilioInbox } from 'entities/Call/model/TwilioInbox'
import { type TwilioConversation } from 'entities/Call/model/TwilioConversation'
import { CallContact } from 'entities/Call/model/CallContact'

type ICallEntryParams = {
  call: Call
  contact: TwilioContact
  inbox: TwilioInbox
  conversation: TwilioConversation | null
  direction: Call.CallDirection
}

export class CallEntry {
  readonly id = nanoid()
  readonly contact: TwilioContact
  readonly inbox: TwilioInbox
  readonly conversation: TwilioConversation | null
  readonly direction: Call.CallDirection
  readonly addedAt: number
  readonly callContact: CallContact

  private _call: Call
  private _callSid: string
  private _status: Call.State
  private _answeredAt: number | null = null
  private _isHold = false
  private _holdCount = 0
  private _isSdkLegClosed = false
  private _isHoldConfirmed = false
  private _isResumePending = false
  private _isHoldPending = false
  // The call has truly ended server-side (customer hung up a held call) while
  // the agent's SDK leg is still live playing Twilio's "your contact has exited
  // the call" announcement. We defer teardown so the announcement finishes; this
  // flag tells eventConnectDisconnect that the natural leg-close is a real end
  // (remove + hide), not a hold-park to keep.
  private _isEndPending = false

  constructor(params: ICallEntryParams) {
    this._call = params.call
    this._callSid = params.call.parameters.CallSid
    this.contact = params.contact
    this.inbox = params.inbox
    this.conversation = params.conversation
    this.direction = params.direction
    this.addedAt = Date.now()
    this._status = params.call.status()

    this.callContact = new CallContact({
      contact: {
        id: params.contact.id,
        first_name: params.contact.first_name,
        last_name: params.contact.last_name,
        full_name: params.contact.full_name,
        color: params.contact.color,
        formatted_number: params.contact.formatted_number,
        number: params.contact.number,
      },
      call: {
        sid: this.callSid,
        startTime: new Date(this.addedAt).toISOString(),
      },
    })
    this.callContact.inbox_name = params.inbox.name
    this.callContact.inbox_number = params.inbox.number?.formatted_number

    makeAutoObservable(this)
  }

  syncStatus = () => {
    this._status = this._call.status()
    if (this._status === Call.State.Open && this._answeredAt === null) {
      this._answeredAt = Date.now()
    }
  }

  setIsHold = (value: boolean) => {
    if (value && !this._isHold) this._holdCount += 1
    this._isHold = value
  }

  setSdkLegClosed = (value: boolean) => {
    this._isSdkLegClosed = value
  }

  setHoldConfirmed = (value: boolean) => {
    this._isHoldConfirmed = value
  }

  setResumePending = (value: boolean) => {
    this._isResumePending = value
  }

  setHoldPending = (value: boolean) => {
    this._isHoldPending = value
  }

  setEndPending = (value: boolean) => {
    this._isEndPending = value
  }

  setCallSid = (sid: string) => {
    this._callSid = sid
    this.callContact.sid = sid
  }

  replaceCall = (newCall: Call) => {
    this._call = newCall
    this._status = newCall.status()
    this._isSdkLegClosed = false
    this._isEndPending = false
  }

  dispose = () => {
    this.callContact.clearIncrementTime()
  }

  get call() {
    return this._call
  }

  get callSid() {
    return this._callSid
  }

  get status() {
    return this._status
  }

  get answeredAt() {
    return this._answeredAt
  }

  get isHold() {
    return this._isHold
  }

  get isHoldConfirmed() {
    return this._isHoldConfirmed
  }

  get isSdkLegClosed() {
    return this._isSdkLegClosed
  }

  get isResumePending() {
    return this._isResumePending
  }

  get isHoldPending() {
    return this._isHoldPending
  }

  get isEndPending() {
    return this._isEndPending
  }

  get isRinging() {
    return this._status === Call.State.Pending || this._status === Call.State.Ringing
  }

  get isOpen() {
    return this._status === Call.State.Open
  }

  get isActive() {
    if (this._isHold) return false
    if (this._isSdkLegClosed) return false
    if (this._isEndPending) return false
    return this._status === Call.State.Open || this._status === Call.State.Connecting
  }

  get isClosed() {
    return this._status === Call.State.Closed
  }

  get isFirstHold() {
    return this._holdCount === 0
  }

  get isIncoming() {
    return this.direction === Call.CallDirection.Incoming
  }
}
